import React, { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import './inputs.scss';

/**
 * FancyAutocomplete — wraps MUI Autocomplete in our visual system.
 *
 *   <FancyAutocomplete
 *     label="Friends"
 *     multiple
 *     options={Object.keys(userFriends)}
 *     getOptionLabel={(o) => userFriends[o].name}
 *     value={friendIds}
 *     onChange={(newValue) => setFriendIds(newValue)}
 *     fullWidth
 *   />
 *
 * Props are passthrough to MUI Autocomplete except:
 *   - onChange receives just (newValue) instead of (event, newValue) for ergonomic compat
 *   - label becomes the floating label
 *   - fullWidth toggles a 100% width
 */
export const FancyAutocomplete = ({
  label,
  multiple = false,
  options = [],
  getOptionLabel = (o) => (typeof o === 'string' ? o : o?.label ?? ''),
  value,
  onChange,
  placeholder,
  helperText,
  error = false,
  disabled = false,
  fullWidth = false,
  limitTags,
  filterSelectedOptions = false,
  disablePortal = false,
  style,
  className = '',
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const appWidth = useSelector(state=>state.appWidth);
  const isMobile = appWidth <= 560;
  const hasTags = multiple && Array.isArray(value) && value.length > 0;
  const filled = hasTags || (!multiple && value != null && value !== '');
  const floating = focused || filled;

  const wrapperClasses = [
    'fancy-autocomplete',
    fullWidth ? 'fancy-autocomplete--full' : '',
    hasTags ? 'fancy-autocomplete--has-tags' : '',
    className,
  ].filter(Boolean).join(' ');

  const fieldClasses = [
    'fancy-tf',
    'fancy-tf--full',
    error ? 'fancy-tf--error' : '',
    disabled ? 'fancy-tf--disabled' : '',
    focused ? 'fancy-tf--focused' : '',
    floating ? 'fancy-tf--floating' : '',
    label ? 'fancy-tf--has-label' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses} style={style}>
      <Autocomplete
        multiple={multiple}
        options={options}
        getOptionLabel={getOptionLabel}
        value={value ?? (multiple ? [] : null)}
        onChange={(event, newValue) => onChange && onChange(newValue, event)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        filterSelectedOptions={filterSelectedOptions}
        disableCloseOnSelect={multiple}
        blurOnSelect={!multiple}
        disablePortal={disablePortal}
        disabled={disabled}
        limitTags={limitTags}
        slotProps={{
          popper: { className: 'fancy-autocomplete-popper' },
        }}
        renderTags={(tagValue, getTagProps) =>
          {
            const effectiveLimitTags = multiple && isMobile
              ? 1
              : limitTags;
            const visibleTags = multiple && effectiveLimitTags && tagValue.length > effectiveLimitTags
              ? tagValue.slice(0, effectiveLimitTags)
              : tagValue;
            const hiddenCount = tagValue.length - visibleTags.length;

            return <div className="fancy-chip-container">
              {visibleTags.map((option, index) => {
              const tagProps = getTagProps({ index });
              // Strip the default key MUI injects; we use option-derived keys.
              const { key, onDelete, className: muiTagClassName = '', ...restProps } = tagProps;
              return (
                <span key={key} {...restProps} className={['fancy-chip', muiTagClassName].filter(Boolean).join(' ')}>
                  <span className="fancy-chip__label">{getOptionLabel(option)}</span>
                  <button
                    type="button"
                    className="fancy-chip__remove"
                    aria-label={`Remove ${getOptionLabel(option)}`}
                    onClick={(e) => { e.stopPropagation(); onDelete(e); }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                </span>
              );
              })}
              {hiddenCount > 0 && (
                <span className="fancy-chip fancy-chip--count">+{hiddenCount}</span>
              )}
            </div>;
          }
        }
        renderOption={(optionProps, option, { selected }) => (
          <li
            {...optionProps}
            className={[
              optionProps.className,
              selected ? 'fancy-autocomplete-option--selected' : '',
            ].filter(Boolean).join(' ')}
          >
            <span className="fancy-autocomplete-option__label">
              {getOptionLabel(option)}
            </span>
            {selected && <CheckIcon className="fancy-autocomplete-option__check" fontSize="small" />}
          </li>
        )}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, inputProps, id } = params;
          const {
            className: muiInputClassName = '',
            ...inputPropsWithoutClassName
          } = inputProps;
          return (
            <div className={fieldClasses}>
              <div className="fancy-tf__field" ref={InputProps.ref}>
                <div className="fancy-tf__inner">
                  {label && (
                    <label htmlFor={id} className="fancy-tf__label" {...InputLabelProps}>
                      {label}
                    </label>
                  )}
                  {InputProps.startAdornment /* selected chips render here */}
                  <input
                    {...inputPropsWithoutClassName}
                    id={id}
                    className={['fancy-tf__input', muiInputClassName].filter(Boolean).join(' ')}
                    placeholder={floating && !hasTags ? placeholder : ''}
                  />
                </div>
                {InputProps.endAdornment /* clear / popup indicator */}
              </div>
              {helperText && (
                <div className={`fancy-tf__helper ${error ? 'fancy-tf__helper--error' : ''}`}>{helperText}</div>
              )}
            </div>
          );
        }}
        {...rest}
      />
    </div>
  );
};
